/* ==========================================================================
   Reia Video Testimonials
   Swiper init (dynamic import) + full-screen swipeable modal slideshow
   ========================================================================== */

function initSection(section) {
  const swiperEl = section.querySelector('.reia-vt__swiper');
  const modal = section.querySelector('[data-modal]');
  const modalSwiperEl = modal.querySelector('[data-modal-swiper]');
  const modalSlidesWrap = modal.querySelector('[data-modal-slides]');
  const closeBtns = modal.querySelectorAll('[data-modal-close]');
  const expertUrl = section.dataset.expertUrl || '';
  const expertAvatarUrl = section.dataset.expertAvatar || '';
  const prefetched = new Set();
  let lastFocused = null;
  let modalSwiper = null;
  let Lib = null;
  let modalMuted = false;

  /* ----- Thumbnails: set poster only when the section nears the viewport --- */
  // Reia: <video poster> can't be lazy-loaded natively, so the markup ships data-poster.
  const revealPosters = () => {
    section.querySelectorAll('video[data-poster]').forEach((v) => {
      v.poster = v.dataset.poster;
      v.removeAttribute('data-poster');
    });
  };
  if ('IntersectionObserver' in window) {
    const posterSectionObserver = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        posterSectionObserver.disconnect();
        revealPosters();
      }
    }, { rootMargin: '600px 0px' });
    posterSectionObserver.observe(section);
  } else {
    revealPosters();
  }

  /* ----- Swiper init (dynamic import from theme chunk) ------------------- */

  import(flu.chunks.swiper).then((mod) => {
    Lib = mod;
    const { Swiper, Navigation, Keyboard } = mod;

    // Reia: plain, uniform-size carousel - no coverflow/depth effect, no
    // centered-slide balancing, no loop/rewind. Arrows just slide back and
    // forth through the real videos and disable at the true first/last.
    new Swiper(swiperEl, {
      modules: [Navigation, Keyboard],
      grabCursor: true,
      slidesPerView: 'auto',
      spaceBetween: 16,
      watchOverflow: true,
      speed: 600,
      cssMode: false,
      watchSlidesProgress: true,
      a11y: {
        prevSlideMessage: 'Previous video',
        nextSlideMessage: 'Next video',
      },
      navigation: {
        prevEl: section.querySelector('.reia-vt__nav--prev'),
        nextEl: section.querySelector('.reia-vt__nav--next'),
      },
      keyboard: { enabled: true, onlyInViewport: true },
    });

    // After Swiper inits (and clones slides for loop), lazy-load poster
    // videos via per-element IntersectionObserver
    initPosterLazyLoad();
  });

  /* ----- Lazy-load poster videos per card (skips Swiper loop clones) ----- */

  function initPosterLazyLoad() {
    const posterObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) {
          // Swap data-src → src on first intersection
          if (!video.dataset.loaded) {
            video.querySelectorAll('source[data-src]').forEach((src) => {
              src.src = src.dataset.src;
            });
            video.load();
            video.dataset.loaded = '1';
          }
          // Only play if modal is not open (free up decoders for modal)
          if (modal.hidden) {
            video.play().catch(() => {});
          }
        } else {
          video.pause();
        }
      });
    }, { rootMargin: '200px' });

    // Reia: loop:true clones each real slide (including its <video>) to fill
    // the loop edges. Without this filter, both the original and its clone(s)
    // would independently fetch and play the same video at once. Only the
    // real (non-duplicate) slide's video is observed/loaded/played.
    section.querySelectorAll('[data-poster-video]').forEach((v) => {
      if (v.closest('.swiper-slide-duplicate')) return;
      posterObserver.observe(v);
    });
  }

  /* ----- Pause / resume poster videos ------------------------------------ */

  function pauseAllPosterVideos() {
    section.querySelectorAll('[data-poster-video]').forEach((v) => {
      v.pause();
    });
  }

  function resumeVisiblePosterVideos() {
    section.querySelectorAll('[data-poster-video]').forEach((v) => {
      if (v.dataset.loaded) {
        v.play().catch(() => {});
      }
    });
  }

  /* ----- Prefetch modal video on hover/touch ----------------------------- */

  section.querySelectorAll('.reia-vt-card__btn').forEach((btn) => {
    const doPrefetch = () => {
      const src = btn.dataset.videoSrc;
      if (src && !prefetched.has(src)) {
        prefetched.add(src);
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = src;
        link.as = 'video';
        document.head.appendChild(link);
      }
    };
    btn.addEventListener('pointerenter', doPrefetch, { once: true });
  });

  /* ----- Collect unique video data for modal ----------------------------- */

  function getUniqueVideos() {
    const cards = section.querySelectorAll('.reia-vt-card__btn');
    const videos = [];
    const seen = new Set();

    cards.forEach((btn) => {
      const src = btn.dataset.videoSrc;
      if (!src || seen.has(src)) return;
      seen.add(src);
      videos.push({
        src,
        ctaText: btn.dataset.ctaText || '',
        ctaUrl: btn.dataset.ctaUrl || '',
        title: btn.dataset.title || '',
        igUrl: btn.dataset.instagramUrl || '',
        viewCount: btn.dataset.viewCount || '',
      });
    });
    return videos;
  }

  /* ----- Modal logic (full-screen slideshow) ----------------------------- */

  function pauseAllModalVideos() {
    modal.querySelectorAll('.reia-vt-modal__video').forEach((v) => {
      v.pause();
    });
  }

  function openModal(triggerBtn) {
    modalMuted = false;
    const clickedSrc = triggerBtn.dataset.videoSrc;
    const igUrl = triggerBtn.dataset.instagramUrl;

    if (!clickedSrc) {
      if (igUrl) window.open(igUrl, '_blank', 'noopener');
      return;
    }
    if (!Lib) return;

    // Pause all poster videos to free up decoder bandwidth for modal
    pauseAllPosterVideos();

    lastFocused = triggerBtn;
    const videos = getUniqueVideos();
    let startIndex = videos.findIndex((v) => v.src === clickedSrc);
    if (startIndex < 0) startIndex = 0;

    // Build slides — videos start without src (lazy-loaded per slide)
    modalSlidesWrap.innerHTML = videos
      .map((v) => {
        let cta = '';
        if (v.ctaText && v.ctaUrl) {
          const isExternal = /^https?:\/\//.test(v.ctaUrl);
          cta = '<a class="reia-vt-modal__cta-btn" href="' + v.ctaUrl + '"' +
            (isExternal ? ' target="_blank" rel="noopener noreferrer"' : '') +
            '>' + v.ctaText + '</a>';
        }
        const views = v.viewCount
          ? '<span class="reia-vt-modal__views">' +
            '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> ' +
            v.viewCount + '</span>'
          : '';

        const expertPill = expertUrl
          ? '<button class="reia-vt-modal__expert-pill" data-talk-expert data-reia-booking-open type="button" aria-label="Talk to expert">' +
              '<span class="reia-vt-modal__expert-cam">' +
                '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>' +
              '</span>' +
              '<span class="reia-vt-modal__expert-info">' +
                '<span class="reia-vt-modal__expert-title">Talk to Expert</span>' +
                '<span class="reia-vt-modal__expert-status">Available now</span>' +
              '</span>' +
              (expertAvatarUrl
                ? '<span class="reia-vt-modal__expert-avatar">' +
                    '<img src="' + expertAvatarUrl + '" alt="Expert" width="36" height="36" loading="lazy">' +
                    '<span class="reia-vt-modal__online-dot"></span>' +
                  '</span>'
                : '') +
            '</button>'
          : '';

        return '<div class="swiper-slide reia-vt-modal__slide">' +
          '<div class="reia-vt-modal__slide-wrap">' +
          '<video class="reia-vt-modal__video" playsinline preload="none" data-src="' + v.src + '"></video>' +
          '<div class="reia-vt-modal__loader" data-loader>' +
            '<div class="reia-vt-modal__spinner"></div>' +
          '</div>' +
          '<div class="reia-vt-modal__slide-overlays">' +
          views + expertPill + cta +
          '</div></div></div>';
      })
      .join('');

    modal.hidden = false;
    document.body.classList.add('reia-vt-modal-open');

    // Load & play a slide's video; show loader until playing
    function activateSlide(swiper, index) {
      const slides = swiper.slides;
      const slide = slides[index];
      if (!slide) return;

      const activeVid = slide.querySelector('.reia-vt-modal__video');
      const loader = slide.querySelector('[data-loader]');
      if (!activeVid) return;

      const hideLoader = () => {
        if (loader) loader.hidden = true;
      };

      // Check if this video is already loaded and ready
      const needsLoad = !activeVid.src || activeVid.src === location.href;

      if (needsLoad) {
        // Show loader for fresh load
        if (loader) loader.hidden = false;
        activeVid.src = activeVid.dataset.src;
        activeVid.preload = 'auto';
      } else if (activeVid.readyState >= 3) {
        // HAVE_FUTURE_DATA or better — video is ready, no loader needed
        hideLoader();
      } else {
        // src is set but still buffering
        if (loader) loader.hidden = false;
      }

      activeVid.muted = modalMuted;
      activeVid.currentTime = 0;

      // Hide loader when video starts playing or on error
      activeVid.addEventListener('playing', hideLoader, { once: true });
      activeVid.addEventListener('error', hideLoader, { once: true });

      activeVid.play().catch(() => {
        hideLoader();
      });

      // Preload adjacent slides fully so swipe feels instant
      [index - 1, index + 1].forEach((i) => {
        if (i < 0 || i >= slides.length) return;
        const vid = slides[i] && slides[i].querySelector('.reia-vt-modal__video');
        if (vid && (!vid.src || vid.src === location.href)) {
          vid.src = vid.dataset.src;
          vid.preload = 'auto';
        }
      });
    }

    // Init modal Swiper
    const { Swiper, Navigation, Keyboard } = Lib;

    if (modalSwiper) {
      modalSwiper.destroy(true, true);
    }

    modalSwiper = new Swiper(modalSwiperEl, {
      modules: [Navigation, Keyboard],
      initialSlide: startIndex,
      slidesPerView: 1,
      spaceBetween: 20,
      speed: 300,
      loop: true,
      keyboard: { enabled: true },
      navigation: {
        prevEl: modal.querySelector('.reia-vt-modal__nav--prev'),
        nextEl: modal.querySelector('.reia-vt-modal__nav--next'),
      },
      on: {
        // Pause audio immediately when swipe starts (no 300ms gap)
        slideChange: function () {
          pauseAllModalVideos();
        },
        // Activate new slide after transition completes
        slideChangeTransitionEnd: function () {
          activateSlide(this, this.activeIndex);
        },
      },
    });

    // Load and play the initial slide immediately
    // Use activeIndex (not startIndex) because loop:true prepends clones,
    // shifting the DOM indices so slides[startIndex] != the visible slide.
    activateSlide(modalSwiper, modalSwiper.activeIndex);

    // Focus close button
    const focusClose = modal.querySelector('.reia-vt-modal__close');
    if (focusClose) focusClose.focus();
  }

  function closeModal() {
    if (modalSwiper) {
      modal.querySelectorAll('.reia-vt-modal__video').forEach((v) => {
        v.pause();
        v.removeAttribute('src');
        v.load();
      });
      modalSwiper.destroy(true, true);
      modalSwiper = null;
    }
    modalSlidesWrap.innerHTML = '';
    modal.hidden = true;
    document.body.classList.remove('reia-vt-modal-open');

    // Resume poster videos now that modal is closed
    resumeVisiblePosterVideos();

    if (lastFocused) {
      lastFocused.focus();
      lastFocused = null;
    }
  }

  // Wire up card clicks
  section.querySelectorAll('.reia-vt-card__btn').forEach((btn) => {
    btn.addEventListener('click', () => openModal(btn));
  });

  // Close handlers
  closeBtns.forEach((b) => b.addEventListener('click', closeModal));

  // Esc to close (share overlay first, then video modal)
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.hidden) {
      if (shareOverlay && !shareOverlay.hidden) {
        closeShareOverlay();
      } else {
        closeModal();
      }
    }
  });

  // Focus trap inside modal
  modal.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab' || modal.hidden) return;
    const focusables = modal.querySelectorAll(
      'button, a[href], video, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* ----- Mute / Unmute toggle --------------------------------------------- */

  const ICON_UNMUTED = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>';
  const ICON_MUTED = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>';

  function updateMuteIcons() {
    modal.querySelectorAll('[data-mute-toggle]').forEach((btn) => {
      btn.innerHTML = modalMuted ? ICON_MUTED : ICON_UNMUTED;
      btn.setAttribute('aria-label', modalMuted ? 'Unmute' : 'Mute');
    });
  }

  /* ----- Share overlay ---------------------------------------------------- */

  const shareOverlay = modal.querySelector('[data-vt-share-overlay]');
  const shareToast = modal.querySelector('[data-vt-share-toast]');

  function openShareOverlay() {
    if (!shareOverlay) return;
    shareOverlay.hidden = false;
    requestAnimationFrame(() => {
      shareOverlay.classList.add('is-visible');
    });
  }

  function closeShareOverlay() {
    if (!shareOverlay) return;
    shareOverlay.classList.remove('is-visible');
    shareOverlay.addEventListener('transitionend', function handler() {
      shareOverlay.hidden = true;
      shareOverlay.removeEventListener('transitionend', handler);
    });
  }

  function copyShareLink(url) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        showShareToast();
        closeShareOverlay();
      });
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      showShareToast();
      closeShareOverlay();
    }
  }

  function showShareToast() {
    if (!shareToast) return;
    shareToast.hidden = false;
    requestAnimationFrame(() => {
      shareToast.classList.add('is-visible');
    });
    setTimeout(() => {
      shareToast.classList.remove('is-visible');
      shareToast.addEventListener('transitionend', function handler() {
        shareToast.hidden = true;
        shareToast.removeEventListener('transitionend', handler);
      });
    }, 2000);
  }

  // Event delegation for dynamically built modal slides
  modal.addEventListener('click', (e) => {
    // Talk to Expert — handled by data-reia-booking-open attribute
    const expertBtn = e.target.closest('[data-talk-expert]');
    if (expertBtn) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'reia_talk_to_expert_clicked',
        source: 'video_testimonials'
      });
      return;
    }

    // Mute / Unmute
    const muteBtn = e.target.closest('[data-mute-toggle]');
    if (muteBtn) {
      modalMuted = !modalMuted;
      const activeSlide = modalSwiper && modalSwiper.slides[modalSwiper.activeIndex];
      const vid = activeSlide && activeSlide.querySelector('.reia-vt-modal__video');
      if (vid) vid.muted = modalMuted;
      updateMuteIcons();
      return;
    }

    // Share
    if (e.target.closest('[data-share]')) {
      openShareOverlay();
      return;
    }

    // Share close
    if (e.target.closest('[data-vt-share-close]')) {
      closeShareOverlay();
      return;
    }

    // Copy link
    const copyBtn = e.target.closest('[data-vt-share-copy]');
    if (copyBtn) {
      copyShareLink(copyBtn.dataset.url);
      return;
    }

    // Close share when clicking overlay backdrop
    if (e.target === shareOverlay) {
      closeShareOverlay();
    }
  });
}

function init() {
  document.querySelectorAll('.reia-vt[data-section-id]').forEach(initSection);
}

init();

// Re-init in Shopify theme editor when section is updated
document.addEventListener('shopify:section:load', (e) => {
  const section = e.target.querySelector('.reia-vt[data-section-id]');
  if (section) initSection(section);
});
